import React, { useState } from 'react';
import { 
  Box, 
  Typography, 
  TextField, 
  InputAdornment, 
  CircularProgress,
  Card,
  CardContent,
  CardMedia,
  Tabs,
  Tab,
  Button,
  Paper,
  FormControlLabel,
  Switch,
  Alert,
  Snackbar,
  useMediaQuery,
  useTheme,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from '@mui/material';
import { Search as SearchIcon, SmartToy as SmartToyIcon, ExpandMore as ExpandMoreIcon } from '@mui/icons-material';
import { useSpotify, useAI } from '../context';
import Layout from '../components/layout/Layout';
import { searchAgentService, AgentResult, FunctionCall } from '../services/searchAgent';
import { ChatMessage } from '../services/openaiService';
import { SearchCommandInput, AgentResponse } from '../components/playlistAgent';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`search-tabpanel-${index}`}
      aria-labelledby={`search-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ pt: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

const Search: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { search, isLoading } = useSpotify();
  const { isLoading: isLoadingAI } = useAI();
  
  // Regular search state
  const [query, setQuery] = useState('');
  const [searchResults, setSearchResults] = useState<any>({
    tracks: [],
    artists: [],
    albums: [],
    playlists: []
  });
  const [tabValue, setTabValue] = useState(0);
  const [hasSearched, setHasSearched] = useState(false);
  
  // AI search state
  const [useAISearch, setUseAISearch] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [aiResults, setAiResults] = useState<AgentResult[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [currentCommand, setCurrentCommand] = useState('');
  const [functionCalled, setFunctionCalled] = useState('');
  const [functionParameters, setFunctionParameters] = useState<Record<string, any>>({});
  const [showAgentResponse, setShowAgentResponse] = useState(false);
  const [functionCalls, setFunctionCalls] = useState<FunctionCall[]>([]);
  const [openaiResponse, setOpenaiResponse] = useState<string>('');
  const [conversationHistory, setConversationHistory] = useState<ChatMessage[]>([]);

  const handleSearch = async () => {
    if (!query.trim()) return;
    
    try {
      const results = await search(query, 'track,artist,album,playlist');
      setSearchResults(results);
      setHasSearched(true);
    } catch (error) {
      console.error('Search error:', error);
    }
  };

  // Handle AI search command
  const handleAISearch = async (command: string) => {
    if (!command.trim()) return;
    
    setIsProcessing(true);
    setError(null);
    setAiResults([]);
    setCurrentCommand(command);
    
    try {
      // Clear previous history
      searchAgentService.clearHistory();
      
      // Process the command
      const commandResults = await searchAgentService.processCommand(command, false);
      
      // Get the function call history
      const callHistory = searchAgentService.getFunctionCallHistory();
      
      // Get the conversation history
      const history = searchAgentService.getConversationHistory();
      
      setAiResults(commandResults);
      setFunctionCalls(callHistory);
      setConversationHistory(history);
      
      // Set the OpenAI response from the last assistant message
      const lastAssistantMessage = history.filter((msg: ChatMessage) => msg.role === 'assistant').pop();
      if (lastAssistantMessage) {
        setOpenaiResponse(lastAssistantMessage.content || '');
        
        // Get the actual function name from the first function call if available
        if (callHistory.length > 0) {
          setFunctionCalled(callHistory[0].function);
          setFunctionParameters(callHistory[0].parameters || {});
        } else {
          setFunctionCalled('OpenAI Function Calling');
          setFunctionParameters({});
        }
      }
      
      setSnackbarMessage(`Executed ${commandResults.length} functions successfully`);
      setSnackbarOpen(true);
    } catch (err: any) {
      console.error('Error processing command:', err);
      setError(err.message || 'An error occurred while processing your command');
    } finally {
      setIsProcessing(false);
      // Keep agent response visible
      setShowAgentResponse(true);
    }
  };

  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      handleSearch();
    }
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const renderTrackItem = (track: any, index: number) => (
    <Card key={`track-${track.id}-${index}`} sx={{ display: 'flex', mb: isMobile ? 1 : 2 }} className="track-card">
      {track.imageUrl ? (
        <CardMedia
          component="img"
          sx={{ width: isMobile ? 60 : 80, height: isMobile ? 60 : 80 }}
          image={track.imageUrl}
          alt={track.name}
        />
      ) : (
        <Box 
          className="placeholder-album"
          sx={{ width: isMobile ? 60 : 80, height: isMobile ? 60 : 80 }}
        />
      )}
      <CardContent sx={{ 
        flex: '1 0 auto', 
        py: isMobile ? 0.5 : 1,
        px: isMobile ? 1 : 2,
        '&:last-child': { pb: isMobile ? 0.5 : 1 }
      }}>
        <Typography variant={isMobile ? "body1" : "subtitle1"} component="div" noWrap>
          {track.name}
        </Typography>
        <Typography variant="body2" color="text.secondary" noWrap>
          {track.artists.map((artist: any) => artist.name).join(', ')}
        </Typography>
        <Typography variant="caption" color="text.secondary" className={isMobile ? "hide-on-mobile" : ""}>
          {track.album.name}
        </Typography>
      </CardContent>
    </Card>
  );

  const renderArtistItem = (artist: any, index: number) => (
    <Card key={`artist-${artist.id}-${index}`} sx={{ display: 'flex', mb: isMobile ? 1 : 2 }} className="artist-card">
      {artist.imageUrl ? (
        <CardMedia
          component="img"
          sx={{ 
            width: isMobile ? 60 : 80, 
            height: isMobile ? 60 : 80, 
            borderRadius: '50%' 
          }}
          image={artist.imageUrl}
          alt={artist.name}
        />
      ) : (
        <Box 
          className="placeholder-artist"
          sx={{ 
            width: isMobile ? 60 : 80, 
            height: isMobile ? 60 : 80 
          }}
        />
      )}
      <CardContent sx={{ 
        flex: '1 0 auto', 
        py: isMobile ? 0.5 : 1,
        px: isMobile ? 1 : 2,
        '&:last-child': { pb: isMobile ? 0.5 : 1 }
      }}>
        <Typography variant={isMobile ? "body1" : "subtitle1"} component="div">
          {artist.name}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Artist
        </Typography>
      </CardContent>
    </Card>
  );

  const renderAlbumItem = (album: any, index: number) => (
    <Card key={`album-${album.id}-${index}`} sx={{ display: 'flex', mb: isMobile ? 1 : 2 }} className="album-card">
      {album.images?.[0]?.url ? (
        <CardMedia
          component="img"
          sx={{ width: isMobile ? 60 : 80, height: isMobile ? 60 : 80 }}
          image={album.images[0].url}
          alt={album.name}
        />
      ) : (
        <Box 
          className="placeholder-album"
          sx={{ width: isMobile ? 60 : 80, height: isMobile ? 60 : 80 }}
        />
      )}
      <CardContent sx={{ 
        flex: '1 0 auto', 
        py: isMobile ? 0.5 : 1,
        px: isMobile ? 1 : 2,
        '&:last-child': { pb: isMobile ? 0.5 : 1 }
      }}>
        <Typography variant={isMobile ? "body1" : "subtitle1"} component="div" noWrap>
          {album.name}
        </Typography>
        <Typography variant="body2" color="text.secondary" noWrap>
          {album.artists.map((artist: any) => artist.name).join(', ')}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          Album • {album.totalTracks} tracks
        </Typography>
      </CardContent>
    </Card>
  );

  const renderPlaylistItem = (playlist: any, index: number) => (
    <Card key={`playlist-${playlist.id}-${index}`} sx={{ display: 'flex', mb: isMobile ? 1 : 2 }} className="playlist-card">
      {playlist.imageUrl ? (
        <CardMedia
          component="img"
          sx={{ width: isMobile ? 60 : 80, height: isMobile ? 60 : 80 }}
          image={playlist.imageUrl}
          alt={playlist.name}
        />
      ) : (
        <Box 
          className="placeholder-playlist"
          sx={{ width: isMobile ? 60 : 80, height: isMobile ? 60 : 80 }}
        />
      )}
      <CardContent sx={{ 
        flex: '1 0 auto', 
        py: isMobile ? 0.5 : 1,
        px: isMobile ? 1 : 2,
        '&:last-child': { pb: isMobile ? 0.5 : 1 }
      }}>
        <Typography variant={isMobile ? "body1" : "subtitle1"} component="div" noWrap>
          {playlist.name}
        </Typography>
        <Typography variant="body2" color="text.secondary" noWrap>
          {playlist.description || 'Playlist'}
        </Typography>
        <Typography variant="caption" color="text.secondary" className={isMobile ? "hide-on-mobile" : ""}>
          {playlist.owner.displayName} • {playlist.totalTracks} tracks
        </Typography>
      </CardContent>
    </Card>
  );

  // Render displayed tracks
  const renderDisplayedTracks = (tracks: any[], title?: string) => {
    return (
      <Paper sx={{ p: isMobile ? 1.5 : 2, mb: 2 }} className="tracks-display-container">
        {title && (
          <Typography variant={isMobile ? "subtitle1" : "h6"} gutterBottom>
            {title}
          </Typography>
        )}
        
        <Box sx={{ mt: isMobile ? 1 : 2 }} className="tracks-list">
          {tracks.map((track, index) => renderTrackItem(track, index))}
        </Box>
      </Paper>
    );
  };
  
  // Render displayed playlist
  const renderDisplayedPlaylist = (playlist: any) => {
    return (
      <Paper sx={{ p: isMobile ? 1.5 : 2, mb: 2 }} className="playlist-display-container">
        <Box sx={{ 
          display: 'flex', 
          mb: 2,
          flexDirection: isMobile ? 'column' : 'row',
          alignItems: isMobile ? 'center' : 'flex-start'
        }}
        className="playlist-header">
          {playlist.imageUrl ? (
            <CardMedia
              component="img"
              sx={{ 
                width: isMobile ? 100 : 120, 
                height: isMobile ? 100 : 120,
                marginBottom: isMobile ? 1 : 0
              }}
              image={playlist.imageUrl}
              alt={playlist.name}
            />
          ) : (
            <Box 
              className="placeholder-playlist"
              sx={{ 
                width: isMobile ? 100 : 120, 
                height: isMobile ? 100 : 120,
                marginBottom: isMobile ? 1 : 0
              }}
            />
          )}
          
          <Box sx={{ 
            ml: isMobile ? 0 : 2,
            textAlign: isMobile ? 'center' : 'left',
            width: isMobile ? '100%' : 'auto'
          }}>
            <Typography variant={isMobile ? "subtitle1" : "h6"}>{playlist.name}</Typography>
            <Typography variant="body2" color="text.secondary">
              {playlist.description || 'No description'}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {playlist.owner?.displayName} • {playlist.totalTracks || playlist.tracks?.length || 0} tracks
            </Typography>
          </Box>
        </Box>
        
        <Typography variant="subtitle1" gutterBottom sx={{ mt: 2 }}>
          Tracks
        </Typography>
        
        <Box sx={{ mt: 1 }} className="playlist-tracks-container">
          {playlist.tracks?.map((track: any, index: number) => renderTrackItem(track, index))}
        </Box>
      </Paper>
    );
  };

  // Render AI search results
  const renderAIResults = () => {
    if (aiResults.length === 0) return null;
    
    // Separate results by type
    const displayResults = aiResults.filter(result => 
      result.type === 'displayedTracks' || result.type === 'displayedPlaylist'
    );
    
    const searchResults = aiResults.filter(result => 
      result.type !== 'displayedTracks' && result.type !== 'displayedPlaylist'
    );
    
    return (
      <Box sx={{ mt: 3 }}>
        <Typography variant="h6" gutterBottom>
          AI Search Results
        </Typography>
        
        {/* Display tracks and playlists first */}
        {displayResults.map((result, index) => {
          if (result.type === 'displayedTracks') {
            return (
              <Box key={`display-${index}`}>
                {renderDisplayedTracks(result.data.tracks, result.data.title)}
              </Box>
            );
          } else if (result.type === 'displayedPlaylist') {
            return (
              <Box key={`display-${index}`}>
                {renderDisplayedPlaylist(result.data.playlist)}
              </Box>
            );
          }
          return null;
        })}
        
        {/* Show search results in collapsible accordions */}
        {searchResults.length > 0 && (
          <Box sx={{ mt: 3 }}>
            <Typography variant="subtitle1" gutterBottom>
              Raw Search Data
            </Typography>
            
            {searchResults.map((result, index) => (
              <Accordion 
                key={`search-${index}`} 
                sx={{ mb: 2 }}
                defaultExpanded={false}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  aria-controls={`search-result-content-${index}`}
                  id={`search-result-header-${index}`}
                >
                  <Typography>
                    {result.type === 'searchResult' ? 'Search Results' : result.type}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Box 
                    sx={{ 
                      p: 1.5, 
                      bgcolor: 'background.paper', 
                      borderRadius: 1, 
                      fontFamily: 'monospace',
                      fontSize: '0.875rem',
                      overflowX: 'auto',
                      border: '1px solid',
                      borderColor: 'divider'
                    }}
                  >
                    <pre style={{ margin: 0 }}>
                      {JSON.stringify(result.data, null, 2)}
                    </pre>
                  </Box>
                </AccordionDetails>
              </Accordion>
            ))}
          </Box>
        )}
      </Box>
    );
  };

  return (
    <Layout>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" component="h1" sx={{ mb: 3 }}>
          Search
        </Typography>
        
        {/* Search Mode Toggle */}
        <Paper sx={{ 
          p: isMobile ? 1.5 : 2, 
          mb: 3, 
          bgcolor: 'background.paper' 
        }}
        className="search-mode-container">
          <Box sx={{ 
            display: 'flex', 
            flexDirection: isMobile ? 'column' : 'row',
            alignItems: isMobile ? 'flex-start' : 'center', 
            justifyContent: 'space-between' 
          }}>
            <Box sx={{ mb: isMobile ? 2 : 0, width: isMobile ? '100%' : 'auto' }}>
              <Typography variant="subtitle1" gutterBottom>Search Mode</Typography>
              <Typography variant="body2" color="text.secondary">
                Choose how you want to search for music:
              </Typography>
            </Box>
            <FormControlLabel
              control={
                <Switch
                  checked={useAISearch}
                  onChange={(e) => setUseAISearch(e.target.checked)}
                  color="primary"
                  sx={{ 
                    '& .MuiSwitch-switchBase': {
                      padding: isMobile ? 1 : undefined
                    }
                  }}
                />
              }
              label={useAISearch ? "AI Search" : "Standard Search"}
              sx={{ 
                ml: isMobile ? 0 : 2,
                '& .MuiFormControlLabel-label': {
                  fontSize: isMobile ? '0.875rem' : undefined
                }
              }}
            />
          </Box>
        </Paper>
        
        {useAISearch ? (
          // AI Search Interface
          <>
            <SearchCommandInput 
              onExecute={handleAISearch}
              isProcessing={isProcessing}
            />
            
            {/* Agent response visualization */}
            <AgentResponse
              command={currentCommand}
              functionCalled={functionCalled}
              parameters={functionParameters}
              isVisible={showAgentResponse}
              isProcessing={isProcessing}
              onClose={() => setShowAgentResponse(false)}
              openaiResponse={openaiResponse}
            />
            
            {/* Error message */}
            {error && (
              <Alert severity="error" sx={{ mb: 3 }}>
                {error}
              </Alert>
            )}
            
            {/* AI search results */}
            {renderAIResults()}
          </>
        ) : (
          // Standard Search Interface
          <>
            <TextField
              fullWidth
              variant="outlined"
              placeholder={isMobile ? "Search music..." : "Search for songs, artists, albums, or playlists"}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyPress={handleKeyPress}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <Button 
                      variant="contained" 
                      onClick={handleSearch}
                      disabled={isLoading || !query.trim()}
                      size={isMobile ? "small" : "medium"}
                      sx={{ 
                        minWidth: isMobile ? '60px' : '80px',
                        minHeight: isMobile ? '36px' : '40px'
                      }}
                    >
                      Search
                    </Button>
                  </InputAdornment>
                ),
                sx: {
                  fontSize: isMobile ? '0.875rem' : undefined,
                  py: isMobile ? 0.5 : undefined
                }
              }}
              sx={{ mb: 3 }}
              className="search-input"
            />
          </>
        )}
        
        {/* Standard search results - only show when not using AI search */}
        {!useAISearch && (
          <>
            {isLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
                <CircularProgress />
              </Box>
            ) : hasSearched ? (
              <>
                <Tabs 
                  value={tabValue} 
                  onChange={handleTabChange} 
                  aria-label="search results tabs"
                  variant="fullWidth"
                  sx={{
                    '.MuiTab-root': {
                      fontSize: isMobile ? '0.75rem' : undefined,
                      minWidth: isMobile ? 'auto' : undefined,
                      padding: isMobile ? '6px 8px' : undefined
                    }
                  }}
                  className="search-tabs"
                >
                  <Tab label={isMobile ? `Tracks (${searchResults.tracks?.length || 0})` : `Tracks (${searchResults.tracks?.length || 0})`} />
                  <Tab label={isMobile ? `Artists (${searchResults.artists?.length || 0})` : `Artists (${searchResults.artists?.length || 0})`} />
                  <Tab label={isMobile ? `Albums (${searchResults.albums?.length || 0})` : `Albums (${searchResults.albums?.length || 0})`} />
                  <Tab label={isMobile ? `Lists (${searchResults.playlists?.length || 0})` : `Playlists (${searchResults.playlists?.length || 0})`} />
                </Tabs>
                
                <TabPanel value={tabValue} index={0}>
                  {searchResults.tracks?.length > 0 ? (
                    searchResults.tracks.map((track: any, index: number) => renderTrackItem(track, index))
                  ) : (
                    <Typography variant="body1" color="text.secondary" sx={{ textAlign: 'center', my: 4 }}>
                      No tracks found matching "{query}"
                    </Typography>
                  )}
                </TabPanel>
                
                <TabPanel value={tabValue} index={1}>
                  {searchResults.artists?.length > 0 ? (
                    searchResults.artists.map((artist: any, index: number) => renderArtistItem(artist, index))
                  ) : (
                    <Typography variant="body1" color="text.secondary" sx={{ textAlign: 'center', my: 4 }}>
                      No artists found matching "{query}"
                    </Typography>
                  )}
                </TabPanel>
                
                <TabPanel value={tabValue} index={2}>
                  {searchResults.albums?.length > 0 ? (
                    searchResults.albums.map((album: any, index: number) => renderAlbumItem(album, index))
                  ) : (
                    <Typography variant="body1" color="text.secondary" sx={{ textAlign: 'center', my: 4 }}>
                      No albums found matching "{query}"
                    </Typography>
                  )}
                </TabPanel>
                
                <TabPanel value={tabValue} index={3}>
                  {searchResults.playlists?.length > 0 ? (
                    searchResults.playlists.map((playlist: any, index: number) => renderPlaylistItem(playlist, index))
                  ) : (
                    <Typography variant="body1" color="text.secondary" sx={{ textAlign: 'center', my: 4 }}>
                      No playlists found matching "{query}"
                    </Typography>
                  )}
                </TabPanel>
              </>
            ) : (
              <Box sx={{ textAlign: 'center', my: 8 }}>
                <Typography variant="h6" color="text.secondary">
                  Search for music to get started
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  Find your favorite songs, artists, albums, and playlists
                </Typography>
              </Box>
            )}
          </>
        )}
      </Box>
      
      {/* Success message snackbar */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
        message={snackbarMessage}
      />
    </Layout>
  );
};

export default Search;
